import { Singleton } from 'framework/di';

import { PlatformLoader } from '../modules/platform/platform.loader';

@Singleton()
export class NavigationHelper {
  constructor(protected platformLoader: PlatformLoader) {}

  buildUrlWithLocalization(url: string): string {
    if (!url) return '/';

    const routesRoot = this.platformLoader.platform.router.routes?.root || '/';

    return `${routesRoot}${url}`;
  }

  setWindowLocation(url: string): void {
    window.location.href = url;
  }

  /**
   * Check whether a URL is safe for client-side navigation.
   *
   * Blocks dangerous protocols (`javascript:`, `data:`, `vbscript:`, etc.),
   * protocol-relative URLs (`//evil.com`) and backslash variants that browsers
   * normalise to protocol-relative (`/\evil.com`, `\\evil.com`). Only allows
   * `http:`, `https:`, and relative paths.
   *
   * @param url - URL to validate
   * @returns `true` if the URL is safe to navigate to
   */
  static isSafeUrl(url: string): boolean {
    if (typeof url !== 'string') return false;

    const trimmed = url.trim();
    if (!trimmed) return false;

    // Browsers normalise backslashes to forward slashes, so "/\evil.com" or
    // "\\evil.com" resolve to protocol-relative (//evil.com) off-origin navigations.
    // Reject any backslash before the relative-path allowlist below.
    // if (trimmed.includes('\\')) return false;

    // Block protocol-relative URLs (//evil.com)
    if (trimmed.startsWith('//')) return false;

    // Relative paths are safe
    if (trimmed.startsWith('/') || trimmed.startsWith('.') || trimmed.startsWith('#')) return true;

    // For absolute URLs, only allow http/https
    try {
      const parsed = new URL(trimmed, window.location.origin);
      return parsed.protocol === 'http:' || parsed.protocol === 'https:';
    } catch {
      return false;
    }
  }

  handleClickTagAItemBuildUrlWithLocale(event: Event, classesProduct: string): void {
    const target = event?.target as HTMLElement | null;
    let productLink = target?.closest(classesProduct)?.getAttribute('href');

    if (!productLink) return;

    event.preventDefault();

    const routesRoot = this.platformLoader.platform.router.routes?.root || '/';
    const isExternalOrLocalized =
      productLink.startsWith('https://') || productLink.startsWith(routesRoot);

    if (isExternalOrLocalized) {
      this.setWindowLocation(productLink);
      return;
    }

    if (productLink[0] === '/') {
      productLink = productLink.slice(1);
    }

    this.setWindowLocation(this.buildUrlWithLocalization(productLink));
  }
}
